import React, { useState, useEffect } from "react";
import Back from "../components/Common/Back";
import { useSelector, useDispatch } from "react-redux";
import DeliveryForm from "../components/PlaceOrder/DeliveryForm";
import OrderCard from "../components/PlaceOrder/OrderCard";
import OrderPrice from "../components/PlaceOrder/OrderPrice";
import { clearCartOnOrder } from "../redux/menu/cartSlice";
import { fetchProfile } from "../redux/menu/ProfileSlice";
import { isStoreOpen } from "../utils/storeTimeUtils";

export default function Cart() {
  const order = useSelector((state) => state.cart.cart);
  const { profile } = useSelector((state) => state.profile);
  const profileStatus = useSelector((state) => state.profile.status);
  const dispatch = useDispatch();
  
  // Check if store is currently open
  const storeIsOpen = isStoreOpen(profile?.from_time, profile?.to_time);
  
  // Check if store status is available (not false)
  const storeStatusAvailable = profile?.status !== false;
  
  // Store is available for orders if it's open and status is available
  const storeAvailable = storeIsOpen && storeStatusAvailable;
  // Helper function to get delivery details from localStorage
  const getDeliveryDetailsFromStorage = () => {
    try {
      const deliveryData = localStorage.getItem("deliveryDetails");
      if (deliveryData) {
        const parsed = JSON.parse(deliveryData);
        return {
          name: parsed.name || "",
          building: parsed.building || "",
          area: parsed.area || "",
          mobile: parsed.mobile || "",
          kitchenNotes: parsed.kitchenNotes || "",
          orderType: parsed.orderType || "delivery", // Default to delivery
          useCurrentLocation: parsed.useCurrentLocation !== undefined ? parsed.useCurrentLocation : true, // Default to true
        };
      }
    } catch (error) {
      console.error("Error loading delivery details from localStorage:", error);
    }
    return {
      name: "",
      building: "",
      area: "",
      mobile: "",
      kitchenNotes: "",
      orderType: "delivery", // Default to delivery
      useCurrentLocation: true, // Default to true
    };
  };

  // Helper function to save delivery details to localStorage
  const saveDeliveryDetailsToStorage = (deliveryDetails) => {
    try {
      localStorage.setItem("deliveryDetails", JSON.stringify(deliveryDetails));
    } catch (error) {
      console.error("Error saving delivery details to localStorage:", error);
    }
  };

  const [state, setState] = useState({
    form: getDeliveryDetailsFromStorage(),
  });
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    isLocationFetched: false
  });
  const { form } = state;
  const changeHandler = (event) => {
    const { name, value, type, checked } = event.target;
    setState((prev) => {
      const updatedForm = {
        ...prev.form,
        [name]: type === 'checkbox' ? checked : value,
      };
      
      // Save to localStorage whenever form changes
      saveDeliveryDetailsToStorage(updatedForm);
      
      return {
        ...prev,
        form: updatedForm,
      };
    });
  };
  // Check if all required form fields are filled (kitchen notes is optional)
  // For takeaway, only name and mobile are required
  // For delivery, name, building, area, and mobile are required
  const isFormComplete = form.orderType === "takeaway" 
    ? (form.name.trim() !== "" && form.mobile.trim() !== "")
    : (form.name.trim() !== "" && 
       form.building.trim() !== "" && 
       form.area.trim() !== "" && 
       form.mobile.trim() !== "");
  
  // Button is disabled if form is incomplete OR store is not available
  const disabled = !isFormComplete || !storeAvailable;
  // Fetch user location when cart page loads
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            isLocationFetched: true
          });
        },
        (error) => {
          setLocation(prev => ({ ...prev, isLocationFetched: true }));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    } else {
      setLocation(prev => ({ ...prev, isLocationFetched: true }));
    }
  }, []);

  // Fetch profile data on component mount
  useEffect(() => {
    if (profileStatus === 'idle') {
      dispatch(fetchProfile());
    }
  }, [dispatch, profileStatus]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sendMessage = () => {
    let messages = "🛒 *NEW ORDER*\n\n";
    
    // Get phone number from profile, fallback to default if not available
    let phoneNumber = profile.whatsapp; // Default fallback
    
    
    let allPrice = 0;
    let currency = profile?.currency || "AED";
    let totalItems = 0;
    
    // Add order header
    messages += "📋 *ORDER DETAILS*\n";
    messages += "━━━━━━━━━━━━━━━━━━━━\n\n";
    
    // Construct the message with detailed product information
    order.forEach((element, index) => {
      const itemTotal = element.price * element.quantity;
      allPrice += itemTotal;
      totalItems += element.quantity;
      
      messages += `*${index + 1}. ${element.name || element.title}*\n`;
      
      // Add variant info if available
      if (element.variant) {
        messages += `   Size: ${element.variant.name}\n`;
      }
      
      // Add veg/non-veg indicator
      if (element.is_veg !== undefined) {
        messages += `   Type: ${element.is_veg ? '🟢 Veg' : '🔴 Non-Veg'}\n`;
      }
      
      messages += `   Price: ${currency} ${element.price.toFixed(2)}\n`;
      messages += `   Quantity: ${element.quantity}\n`;
      messages += `   Subtotal: ${currency} ${itemTotal.toFixed(2)}\n\n`;
    });

    // Add order summary
    messages += "━━━━━━━━━━━━━━━━━━━━\n";
    messages += `*Total Items: ${totalItems}*\n`;
    messages += `*Total Amount: ${currency} ${allPrice.toFixed(2)}*\n\n`;
    
    // Add customer details
    messages += "👤 *CUSTOMER DETAILS*\n";
    messages += "━━━━━━━━━━━━━━━━━━━━\n";
    messages += `Name: ${form.name}\n`;
    messages += `Order Type: ${form.orderType === 'delivery' ? '🚗 Delivery' : '🏃 Takeaway'}\n`;
    if (form.orderType === 'delivery') {
      messages += `Building/Flat: ${form.building}\n`;
      messages += `Area: ${form.area}\n`;
    }
    messages += `Phone: ${form.mobile}\n`;
    
    // Add kitchen notes if provided
    if (form.kitchenNotes && form.kitchenNotes.trim() !== "") {
      messages += `Kitchen Notes: ${form.kitchenNotes}\n`;
    }
    
    messages += "\n";

    // Add location if available, it's a delivery order, and user wants to share location
    if (form.orderType === 'delivery' && form.useCurrentLocation && location.latitude && location.longitude) {
      messages += "📍 *DELIVERY LOCATION*\n";
      messages += "━━━━━━━━━━━━━━━━━━━━\n";
      const mapUrl = `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
      messages += `Location: ${mapUrl}\n\n`;
    }
    
    // Add order timestamp
    messages += "⏰ *ORDER TIME*\n";
    messages += "━━━━━━━━━━━━━━━━━━━━\n";
    messages += `Date: ${new Date().toLocaleDateString()}\n`;
    messages += `Time: ${new Date().toLocaleTimeString()}\n\n`;
    
    messages += "Thank you! 🙏";

    // Encode the message
    const encodedMessage = encodeURIComponent(messages);

    // Construct the WhatsApp URL
    const url = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    // Open WhatsApp Web or WhatsApp App
    window.open(url, "_blank");
    
    // Save final delivery details to localStorage when order is placed
    saveDeliveryDetailsToStorage(form);
    
    // Clear cart after placing order
    dispatch(clearCartOnOrder());
  };

  return (
    <main className="min-h-screen banner">
      <div className="max-w-screen-xl pt-20 md:pt-24 lg:pt-32 pb-24 md:pb-20 lg:pb-32 mx-auto px-3 md:px-4 lg:px-6">
        <Back />        
        {order.length > 0 ? (
          <>
            {/* Order Type Selection */}
            <div className="mb-4 md:mb-6 lg:mb-8">
              <div className="bg-white/60 backdrop-blur-md rounded-xl md:rounded-2xl shadow-xl border border-white/30 p-3 md:p-4 lg:p-6">
                <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-800 poppins mb-3 md:mb-4">Order Type</h3>
                <div className="flex flex-row gap-2 md:gap-3 lg:gap-4">
                  <button
                    onClick={() => changeHandler({ target: { name: 'orderType', value: 'delivery' } })}
                    className={`flex-1 p-2 md:p-3 lg:p-4 rounded-lg md:rounded-xl transition-all duration-300 border-2 ${
                      form.orderType === 'delivery'
                        ? 'bg-primary text-white border-primary shadow-lg transform scale-[1.02]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-primary hover:bg-primary/5'
                    }`}
                  >
                    <div className="flex flex-col items-center space-y-1 md:space-y-2">
                      <div className="text-base md:text-lg lg:text-2xl">🚗</div>
                      <div className="font-semibold text-xs md:text-sm lg:text-base">Delivery</div>
                      <div className="text-xs md:text-sm opacity-80 text-center hidden md:block">We'll deliver to your location</div>
                    </div>
                  </button>
                  <button
                    onClick={() => changeHandler({ target: { name: 'orderType', value: 'takeaway' } })}
                    className={`flex-1 p-2 md:p-3 lg:p-4 rounded-lg md:rounded-xl transition-all duration-300 border-2 ${
                      form.orderType === 'takeaway'
                        ? 'bg-primary text-white border-primary shadow-lg transform scale-[1.02]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-primary hover:bg-primary/5'
                    }`}
                  >
                    <div className="flex flex-col items-center space-y-1 md:space-y-2">
                      <div className="text-base md:text-lg lg:text-2xl">🏃</div>
                      <div className="font-semibold text-xs md:text-sm lg:text-base">Takeaway</div>
                      <div className="text-xs md:text-sm opacity-80 text-center hidden md:block">Pick up from our location</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
              {/* left side form  */}
              <div className="col-span-1 order-1">
                <div className="bg-white/60 backdrop-blur-md rounded-xl md:rounded-2xl shadow-xl border border-white/30 p-3 md:p-4 lg:p-6 xl:p-8">
                  <DeliveryForm formData={form} handleChange={changeHandler} />
                </div>
              </div>
              {/* right side  */}
              <div className="col-span-1 order-2">
                <div className="bg-white/60 backdrop-blur-md rounded-xl md:rounded-2xl shadow-xl border border-white/30 p-3 md:p-4 lg:p-6 xl:p-8">
                  {/* Cart Header */}
                  <div className="flex items-center justify-between mb-3 md:mb-4 lg:mb-6 pb-2 md:pb-3 lg:pb-4 border-b border-gray-200">
                    <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800 poppins">Your Cart</h2>
                    <div className="bg-primary/10 text-primary px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                      {order.length} {order.length === 1 ? 'item' : 'items'}
                    </div>
                  </div>
                  
                  {/* orders  */}
                  <div className="flex flex-col space-y-2 md:space-y-3 lg:space-y-4 max-h-72 md:max-h-80 lg:max-h-96 overflow-y-auto orderContainer mb-3 md:mb-4 lg:mb-6">
                    {order.map((item) => (
                      <OrderCard key={item.id} {...item} />
                    ))}
                  </div>
                  
                  {/* price  */}
                  <OrderPrice {...order} />
                  
                  {/* place order button  */}
                  <div className="mt-3 md:mt-4 lg:mt-6">
                    {disabled ? (
                      <button
                        disabled="disabled"
                        className="w-full px-3 md:px-4 lg:px-6 py-2.5 md:py-3 lg:py-4 rounded-lg md:rounded-xl bg-gray-300 text-gray-500 poppins font-semibold transition duration-500 opacity-40 cursor-not-allowed text-sm md:text-base"
                      >
                        {!storeAvailable ? 
                          (profile?.status === false ? "Store Currently Not Available" : "Store Currently Closed") :
                          form.name.trim() === "" ? "Enter Name" : 
                          form.mobile.trim() === "" ? "Enter Mobile Number" :
                          form.orderType === "delivery" && form.building.trim() === "" ? "Enter Building/Flat" :
                          form.orderType === "delivery" && form.area.trim() === "" ? "Enter Area" : "Complete All Details"}
                      </button>
                    ) : (
                      <button
                        className="w-full px-3 md:px-4 lg:px-6 py-2.5 md:py-3 lg:py-4 rounded-lg md:rounded-xl bg-gradient-to-r from-red-500 to-pink-500 text-white poppins font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition duration-300 border-2 border-white text-sm md:text-base"
                        onClick={() => {
                          sendMessage();
                        }}
                      >
                         Place Order Now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[50vh] md:min-h-[60vh]">
            <div className="bg-white/60 backdrop-blur-md rounded-xl md:rounded-2xl shadow-xl border border-white/30 p-4 md:p-6 lg:p-12 text-center max-w-sm md:max-w-md mx-auto">
              <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 lg:mb-6">
                <svg className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                </svg>
              </div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 poppins mb-2 md:mb-3 lg:mb-4">
                Your Cart is Empty
              </h1>
              <p className="text-gray-600 mb-3 md:mb-4 lg:mb-6 text-sm md:text-base">
                Add some delicious items to get started!
              </p>
              <button 
                onClick={() => window.history.back()}
                className="bg-primary text-white px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold hover:bg-primary-dark transition duration-300 text-sm md:text-base"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
